import {Button, Group, LoadingOverlay, Paper, Stack, Stepper, useMantineTheme, Text, Code} from "@mantine/core";
import React, {CSSProperties, useState} from "react";
import {AlertCircle, ArrowLeft, ArrowRight, Check, ExclamationMark, X} from "tabler-icons-react";
import Forms from "../Forms";
import useUserStore from "../../../data/User.store";
import LoaderDefault from "../../common/Loader.default/Loader.default";
import BookingApi from "../../../api/Booking/Booking.api";
import {IBookingRequestPo, IBookingRequestRequest} from "../../../api/Booking/Booking.types";
import {useModals} from "@mantine/modals";
import Descriptions from "../../common/Descriptions/Descriptions";
import {IDescriptionsData} from "../../common/Descriptions/Descriptions.types";
import dayjs from "dayjs";

interface Props {
    style?: CSSProperties;
}

function BookingCreate({style}: Props) {
    const theme = useMantineTheme();
    const modals = useModals();
    const {user} = useUserStore();
    const [active, setActive] = useState(0);
    const [multiFormValues, setMultiFormValues] = useState<any>({})
    const [submittingForm, setSubmittingForm] = useState(false);

    function handleSubmitShipper(values: any) {
        setMultiFormValues({
            ...multiFormValues,
            shipper: values
        })
        setActive(active + 1);
    }

    function handleSubmitConsignee(values: any) {
        setMultiFormValues({
            ...multiFormValues,
            consignee: values
        })
        setActive(active + 1);
    }

    function handleSubmitLocation(values: any) {
        setMultiFormValues({
            ...multiFormValues,
            location: values
        })
        setActive(active + 1);
    }

    async function handleSubmitCargo(values: any) {
        setMultiFormValues({
            ...multiFormValues,
            cargo: values
        })

        let endValues = {...multiFormValues, cargo: values};
        setSubmittingForm(true);
        let request: IBookingRequestRequest = {
            shipperId: endValues.shipper.entityID,
            consigneeId: endValues.consignee.entityID,
            originId: endValues.location.origin,
            loadingPortId: endValues.location.loadingPort,
            destinationId: endValues.location.destination,
            commodity: endValues.cargo.commodity,
            specialInstructions: endValues.cargo.specialInstructions,
            cargoReadyDate: endValues.cargo.cargoReadyDate,
            containerUsageType: endValues.cargo.containerUsageType,
            containerCount20: endValues.cargo.containerCount20,
            containerCount40: endValues.cargo.containerCount40,
            containerCount40HQ: endValues.cargo.containerCount40HQ,
            containerCount45: endValues.cargo.containerCount45,
            willProvideISFForm: endValues.cargo.willProvideISFForm,
            willProvideDocuments: endValues.cargo.willProvideDocuments,
            bookingRequestPOs: endValues.cargo.po.split(',').map((poNumber: string) => {
                return {poNumber}
            }) as IBookingRequestPo[]
        }
        let response = await BookingApi.createBooking(request);
        let commonModalProps = {
            centered: true,
        }

        if (response === true) {
            let descriptions:IDescriptionsData[] = [
                {title: "Shipper ID", value: request.shipperId},
                {title: "Consignee ID", value: request.consigneeId},
                {title: "Origin ID", value: request.originId},
                {title: "Loading Port ID", value: request.loadingPortId},
                {title: "Destination ID", value: request.destinationId},
                {title: "Cargo Ready Date", value: dayjs(request.cargoReadyDate).format("ddd, MMM DD, YYYY")},
            ]

            modals.openModal({
                ...commonModalProps,
                title: (
                    <Group spacing="xs">
                        <Check color="#51cf66"/>
                        <Text>Booking created</Text>
                    </Group>
                ),
                children: (
                    <Stack>
                        <Text size="sm">
                            Your booking with the following information has been successfully created:
                        </Text>
                        <Descriptions data={descriptions}/>
                    </Stack>
                ),
            })
        } else if (response.errors) {
            modals.openModal({
                ...commonModalProps,
                title: (
                    <Group spacing="xs">
                        <AlertCircle color="#ff6b6b"/>
                        <Text>Error creating booking</Text>
                    </Group>
                ),
                children: (
                    <Stack>
                        <Text size="sm" align="left">
                            Your booking <i>could not</i> be created due to: <br/>
                            <Text size="sm" color="red">
                                {response.errors.title || response.errors || "No Error Specified"}
                            </Text>
                        </Text>
                        <Text size="sm">
                            If you have gotten this error multiple times please contact support and provide them
                            with this error ID: <br/><Code>{response.errors.traceId || "Booking-Create-No-Error-Code-Specified"}</Code>.
                        </Text>
                    </Stack>

                ),
            })
        }
        setSubmittingForm(false);
    }

    const commonPaperStyle: CSSProperties = {
        position: 'relative',
        width: "100%",
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
        ...style,
    }
    if (!user) {
        return <LoaderDefault text="Loading User..."/>
    }

    return (
        <Stack>
            <LoadingOverlay visible={submittingForm}/>
            <Stepper active={active} onStepClick={setActive} orientation="vertical" size="sm" sx={theme => ({
                display: "flex",
                flexDirection: "row",
                width: "100%",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                ".mantine-Stepper-steps": {
                    marginRight: 48,
                },
                ".mantine-Stepper-content": {
                    paddingTop: 0,
                    width: "100%",
                    maxWidth: 750,
                }
            })}>
                <Stepper.Step label="Step 1" allowStepSelect={active > 0}
                              description="Shipper information">
                    <Paper p="lg" shadow="sm" style={commonPaperStyle}>
                        <Forms.ShipperInformation
                            user={user}
                            initialValues={multiFormValues.shipper}
                            buttons={
                                <Group position="right" mt="xl" align="flex-end">
                                    <Button color="blue" type="submit" rightIcon={<ArrowRight/>}>
                                        Next
                                    </Button>
                                </Group>
                            }
                            onSubmit={handleSubmitShipper}
                        />
                    </Paper>
                </Stepper.Step>
                <Stepper.Step label="Step 2" allowStepSelect={active > 1}
                              description="Consignee information">
                    <Paper p="lg" shadow="sm" style={{...commonPaperStyle}}>
                        <Forms.ConsigneeInformation
                            user={user}
                            initialValues={multiFormValues.consignee}
                            buttons={
                                <Group position="right" mt="xl" align="flex-end">
                                    <Button color="gray" leftIcon={<ArrowLeft/>} onClick={() => setActive(active - 1)}>
                                        Back
                                    </Button>
                                    <Button color="blue" rightIcon={<ArrowRight/>} type="submit">
                                        Next
                                    </Button>
                                </Group>
                            }
                            onSubmit={handleSubmitConsignee}
                        />
                    </Paper>
                </Stepper.Step>
                <Stepper.Step label="Step 3" allowStepSelect={active > 2}
                              description="Location information">
                    <Paper p="lg" shadow="sm" style={{...commonPaperStyle}}>
                        <Forms.LocationInformation
                            user={user}
                            initialValues={multiFormValues.location}
                            shipper={multiFormValues.shipper}
                            consignee={multiFormValues.consignee}
                            buttons={
                                <Group position="right" mt="xl" align="flex-end">
                                    <Button color="gray" leftIcon={<ArrowLeft/>} onClick={() => setActive(active - 1)}>
                                        Back
                                    </Button>
                                    <Button color="blue" rightIcon={<ArrowRight/>} type="submit">
                                        Next
                                    </Button>
                                </Group>
                            }
                            onSubmit={handleSubmitLocation}/>
                    </Paper>
                </Stepper.Step>
                <Stepper.Step label="Step 4" allowStepSelect={active > 3}
                              description="Cargo information">
                    <Paper p="lg" shadow="sm" style={{...commonPaperStyle}}>
                        <Forms.CargoInformation
                            initialValues={multiFormValues.cargo}
                            buttons={
                                <Group position="right" mt="xl" align="flex-end">
                                    <Button color="gray" leftIcon={<ArrowLeft/>} onClick={() => setActive(active - 1)}>
                                        Back
                                    </Button>
                                    <Button color="blue" type="submit" rightIcon={<ArrowRight/>}>
                                        Create Booking
                                    </Button>
                                </Group>
                            }
                            onSubmit={handleSubmitCargo}/>
                    </Paper>
                </Stepper.Step>
            </Stepper>
        </Stack>
    )
}

export default BookingCreate;